import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
// Import Shared Components
import { Loader, MFASetupForm, SubmitButton } from 'shared-components';
import { MFAStorage, MFAErrors, completeMFASetup as genericCompleteMFASetup } from 'shared-components/src/utils/mfa';
import { getSubdomain } from 'shared-components/src/utils/whitelabel';
// Import Local Components
import AuthLayout from '../layouts/Auth';
import { Form, InputContainer } from '../layouts/InnerAuth/styles';
// Import Global Components
import Button from '../../../components/Button';
import { destinationAfterAuth } from '../../../utils/pinSetup';

/**
 * Refactored MFASetup component using reusable MFA components
 * This demonstrates how any portal can use the shared MFA utilities and components
 */
function MFASetup() {
  const intl = useIntl();
  const navigate = useNavigate();

  // Set States
  const [formError, setFormError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mfaData, setMFAData] = useState(null);

  // Generate dynamic branding info
  const getBrandingInfo = () => {
    const { hostname } = window.location;
    const subdomain = getSubdomain(hostname);

    // Use full subdomain for branding, replace dots with spaces
    // For hrblockadvantagecard.staging.payaccount.io -> "hrblockadvantagecard staging Payaccount"
    let companyName;
    if (subdomain && subdomain.toUpperCase() !== 'UNBRANDED') {
      const formattedSubdomain = subdomain.replace(/\./g, ' ');
      companyName = `${formattedSubdomain} Payaccount`;
    } else {
      companyName = 'Berkeley Payaccount';
    }

    return {
      issuer: companyName,
      username: (mfaData && mfaData.user_name) || 'Cardholder', // Use username from backend (actual username for cardholder portal)
      domain: hostname
    };
  };

  useEffect(() => {
    // Get MFA data from session storage (set during login)
    const setupData = MFAStorage.getSetupData();

    if (!setupData) {
      // No setup data available, redirect to login
      navigate('/login', {
        state: {
          error: 'mfa-data-not-available',
          errorDetails: 'MFA setup data not found'
        }
      });
      return;
    }

    setMFAData(setupData);
    setLoading(false);
  }, [navigate]);

  const handleMFASetupComplete = async (userSub, session, totpCode) => {
    try {
      setSubmitting(true);
      setFormError(null);

      // Use the generic MFA setup utility with cardholder-specific endpoint
      await genericCompleteMFASetup(
        '/api/v1/cardholder_portal/mfa/setup',
        userSub,
        session,
        totpCode,
        // onSuccess callback
        () => {
          // Clear MFA session data
          MFAStorage.clearAll();
          // Registration offers a PIN step after every auth factor is done
          window.location.pathname = destinationAfterAuth();
        },
        // onError callback
        (error) => {
          setSubmitting(false);

          // Check if it's an authentication error or validation error
          if (MFAErrors.isAuthError(error)) {
            // Redirect to login for authentication errors
            navigate('/login', {
              state: {
                error: error.message,
                errorDetails: error.details
              }
            });
          } else {
            // Keep on page for validation errors (wrong TOTP code, etc.)
            setFormError(MFAErrors.getUserFriendlyError(error));
          }
        }
      );
    } catch (err) {
      // Error already handled in onError callback
    }
  };

  const handleSkipMFASetup = () => {
    // Clear MFA session data
    MFAStorage.clearAll();
    // Redirect to dashboard, allowing user to skip MFA setup for now
    window.location.pathname = destinationAfterAuth();
  };

  if (loading) {
    return (
      <AuthLayout
        page="mfa-setup"
        heading={intl.messages['mfa-setup-heading']}
        subHeaderOne={intl.messages['mfa-setup-loading']}
      >
        <Loader />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      page="mfa-setup"
      heading={intl.messages['mfa-setup-heading']}
      subHeaderOne={intl.messages['mfa-setup-subheader1-text']}
      subHeaderTwo={intl.messages['mfa-setup-subheader2-text']}
      error={formError}
    >
      {submitting ? (
        <Loader />
      ) : (
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            // Form submission is handled by MFASetupForm onComplete
          }}
        >
          <InputContainer>
            {(() => {
              const brandingInfo = getBrandingInfo();
              return (
                <MFASetupForm
                  mfaData={mfaData}
                  onSetupComplete={handleMFASetupComplete}
                  loading={submitting}
                  error={formError}
                  issuer={brandingInfo.issuer}
                  username={brandingInfo.username}
                  disabled={submitting}
                  autoFocus
                  labels={{
                    qrInstruction: intl.messages['mfa-setup-qr-instruction'] || `Scan this QR code with your authenticator app for ${brandingInfo.issuer}:`,
                    manualEntryLabel: intl.messages['mfa-setup-manual-entry-label'],
                    authenticatorApps: intl.messages['mfa-setup-authenticator-apps'],
                    otpLabel: intl.messages['mfa-totp-code-label'],
                  }}
                />
              );
            })()}
          </InputContainer>

          {/* Primary Submit Button */}
          <SubmitButton
            value={intl.messages['mfa-setup-button']}
            buttonType="primary"
            size="large"
            disabled={submitting}
          />

          {/* Secondary Skip Button */}
          <Button
            buttonText={intl.messages['mfa-verify-skip-button']}
            buttonFor="secondary"
            page="mfa-setup"
            onClick={handleSkipMFASetup}
            variant="text"
            style={{ marginTop: '10px' }}
          />
        </Form>
      )}
    </AuthLayout>
  );
}

export default MFASetup;